import { useEffect } from "react";
import { Dialog, DialogContent, Box, Typography, Stack, IconButton, TextField, Autocomplete } from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { FoodsIcon, Button as FoodsButton } from "@foodpilot/foods";
import { ProductLine, ProductLineForm } from "src/api/client-api/productLines/useProductLines";
import { ProductLineWrite, useCreateProductLine } from "src/api/client-api/productLines/useCreateProductLine";
import { useGetAllProductLines } from "src/api/client-api/productLines/useProductLines";
import { ProductLineGridFormZodSchema, ProductLineFormService } from "src/pages/Settings/ProductLine/ProductLineForm";
import { useSnackbar } from "src/utils/useSnackbar";

type ProductLineModalProps = {
    open: boolean;
    onClose: () => void;
    productLine?: ProductLine | ProductLineForm;
    onSuccess?: (productLine: any) => void;
};

export const ProductLineModal = (props: ProductLineModalProps) => {
    const { open, onClose, productLine, onSuccess } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const { productLines = [] } = useGetAllProductLines();
    const createProductLine = useCreateProductLine();

    type Schema = z.infer<typeof ProductLineGridFormZodSchema>;

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
    } = useForm<Schema>({
        mode: "onChange",
        resolver: zodResolver(ProductLineGridFormZodSchema as any),
    });

    const onSubmitHandler: SubmitHandler<Schema> = (values) => {
        const productLineWrite: ProductLineWrite = ProductLineFormService.getFormToProductLineWrite(values);

        createProductLine.mutate(
            { productLine: productLineWrite },
            {
                onSuccess: (response) => {
                    const savedProductLine = response.data;
                    if (onSuccess) {
                        onSuccess(savedProductLine);
                    }
                    handleClose();
                },
                onError: (error) => {
                    snackbar.setSnackbarProps({
                        message: t("productLine.error.saveFailed", "Failed to save product line. Please try again."),
                        type: "error",
                    });
                },
            },
        );
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            const formData = ProductLineFormService.getProductLineToForm(productLine as ProductLine);
            reset(formData);
        }
    }, [open, productLine, reset]);

    // Filter out the current product line and its descendants to prevent circular references
    const availableParents = productLines.filter((pl) => {
        if (!productLine) return true;
        if (pl.id === productLine.id) return false;
        // Check if this is a descendant of the current line (simplified check)
        if (pl.parent && pl.parent.id === productLine.id) return false;
        return true;
    });

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    maxHeight: "90vh",
                    width: "638px",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px 32px 0px 32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E" }}>
                        {productLine ?
                            t("edit_product_line", "Edit product line")
                        :   t("add_product_line", "Add a product line")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        padding: "8px",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                    }}
                >
                    <FoodsIcon icon="close" size={1.5} />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent
                sx={{
                    padding: "32px",
                    backgroundColor: "#FEFCFB",
                    overflowY: "auto",
                }}
            >
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing="24px">
                        {/* Information Section */}
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "32px" }}
                            >
                                {t("information", "Information")}
                            </Typography>
                            <Stack spacing="24px">
                                {/* Product Line name */}
                                <Box sx={{ width: "100%" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("nom_de_la_gamme", "Product line name")}* :
                                    </Typography>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field: { ref, ...field } }) => (
                                            <TextField
                                                {...field}
                                                inputRef={ref}
                                                placeholder={t("enter_product_line_name", "Enter product line name")}
                                                variant="outlined"
                                                error={Boolean(errors.name)}
                                                helperText={errors.name?.message}
                                                sx={{
                                                    width: "100%",
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "24px",
                                                        backgroundColor: "#FFFFFF",
                                                        border: "1px solid #E7E6E4",
                                                        height: "40px",
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                        "& input": {
                                                            padding: "0px 12px 0px 20px",
                                                            fontWeight: 500,
                                                            fontSize: "0.875rem",
                                                            color: "#393A36",
                                                            "&::placeholder": {
                                                                color: "#A3A2A0",
                                                                opacity: 1,
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                {/* Parent Product Line field */}
                                <Box sx={{ width: "100%" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("parent", "Parent")} :
                                    </Typography>
                                    <Controller
                                        name="parent"
                                        control={control}
                                        render={({ field: { onChange, value } }) => {
                                            const selectedParent = availableParents.find((pl) => pl["@id"] === value);
                                            return (
                                                <Autocomplete
                                                    options={availableParents}
                                                    value={selectedParent || null}
                                                    onChange={(_, newValue) => {
                                                        onChange(newValue ? newValue["@id"] : null);
                                                    }}
                                                    getOptionLabel={(option) => option.name}
                                                    isOptionEqualToValue={(option, value) =>
                                                        option["@id"] === value["@id"]
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder={t(
                                                                "select_parent_product_line",
                                                                "Select parent product line",
                                                            )}
                                                            variant="outlined"
                                                            sx={{
                                                                width: "100%",
                                                                "& .MuiOutlinedInput-root": {
                                                                    borderRadius: "24px",
                                                                    backgroundColor: "#FFFFFF",
                                                                    border: "1px solid #E7E6E4",
                                                                    "& fieldset": {
                                                                        border: "none",
                                                                    },
                                                                    "& input": {
                                                                        padding: "0px 12px 0px 20px",
                                                                        fontWeight: 500,
                                                                        fontSize: "0.875rem",
                                                                        color: "#393A36",
                                                                        "&::placeholder": {
                                                                            color: "#A3A2A0",
                                                                            opacity: 1,
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            );
                                        }}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        {/* Hidden ID field for editing */}
                        <Controller
                            name="id"
                            control={control}
                            render={({ field: { ref, ...field } }) => <input type="hidden" {...field} />}
                        />
                    </Stack>
                </form>
            </DialogContent>

            {/* Footer with buttons */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "28px 32px",
                    borderTop: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box /> {/* Empty box for spacing */}
                <Box sx={{ display: "flex", gap: "12px" }}>
                    <FoodsButton
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            color: "#393A36",
                            borderColor: "#E7E6E4",
                            backgroundColor: "linear-gradient(180deg, #FFFFFF 0%, #F6F5F3 100%)",
                            "&:hover": {
                                backgroundColor: "linear-gradient(180deg, #FFFFFF 0%, #F6F5F3 100%)",
                            },
                        }}
                    >
                        {t("cancel", "Cancel")}
                    </FoodsButton>
                    <FoodsButton
                        variant="contained"
                        type="submit"
                        onClick={handleSubmit(onSubmitHandler)}
                        disabled={!isValid || createProductLine.isLoading}
                        sx={(theme) => ({
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                            boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                            "&:hover": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                color: "white !important",
                            },
                            "&:disabled": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                opacity: 0.36,
                                color: "white !important",
                            },
                        })}
                    >
                        {productLine ? t("save", "Save") : t("add", "Add")}
                    </FoodsButton>
                </Box>
            </Box>
        </Dialog>
    );
};
